import type { IActiveSubscription, ISubscriptionUsage } from "@models";
import {
  useActiveSubscriptionsInfinite,
  useSubscriptionUsageInfinite,
} from "@query";
import { APP_BASE_PATH } from "@shared";
import { useMemo } from "react";
import type { ISubscriptionRow, IUsageRow } from "./types";

function softwareStartFromApi(
  row: IActiveSubscription | ISubscriptionUsage,
): string | undefined {
  return row.softwareStartDate ?? row.billingStartDate;
}

function softwareEndFromApi(
  row: IActiveSubscription | ISubscriptionUsage,
): string | undefined {
  return row.softwareEndDate ?? row.billingEndDate;
}

interface IUseSubscriptionDataOptions {
  page?: number;
  pageSize?: number;
}

export function useSubscriptionData(
  companyUuid: string | undefined,
  options?: IUseSubscriptionDataOptions,
) {
  const { page = 0, pageSize = 10 } = options ?? {};

  // 1. Fetch Infinite Subscriptions
  const { data: subInfiniteData, isLoading: isSubscriptionsLoading } =
    useActiveSubscriptionsInfinite(companyUuid, APP_BASE_PATH, {
      page,
      size: pageSize,
    });

  // 2. Fetch Infinite Usage
  const { data: usageInfiniteData, isLoading: isUsageLoading } =
    useSubscriptionUsageInfinite(companyUuid, APP_BASE_PATH, {
      page,
      size: pageSize,
    });

  const currentSubPage = useMemo(() => {
    const pages = subInfiniteData?.pages ?? [];
    return pages.find((p) => p.number === page) ?? pages[pages.length - 1];
  }, [subInfiniteData, page]);

  const currentUsagePage = useMemo(() => {
    const pages = usageInfiniteData?.pages ?? [];
    return pages.find((p) => p.number === page) ?? pages[pages.length - 1];
  }, [usageInfiniteData, page]);

  // Transform Subscription data
  const subscriptionData = useMemo((): ISubscriptionRow[] => {
    const content = currentSubPage?.content ?? [];
    return content.map((row: IActiveSubscription, index: number) => ({
      id: row.id ?? `sub-${page}-${index}`,
      subscribedTo: row.subscriptionPlanName,
      softwareStartDate: softwareStartFromApi(row),
      softwareEndDate: softwareEndFromApi(row),
      subscriptionSourceBillCode: row.subscriptionSourceBillCode,
      subscriptionInvoiceBillCode: row.subscriptionInvoiceBillCode,
      createdOn: row.createdAt,
      updatedOn: row.updatedAt,
      createdBy: row.createdBy,
      updatedBy: row.updatedBy,
      status: row.isActive ? "Active" : "Inactive",
      originalData: row,
    }));
  }, [currentSubPage, page]);

  // Transform Usage data
  const usageData = useMemo((): IUsageRow[] => {
    const content = currentUsagePage?.content ?? [];
    return content.map((row: ISubscriptionUsage, index: number) => ({
      id: row.id ?? `usage-${page}-${index}`,
      subscribedTo: row.subscriptionPlanName,
      softwareStartDate: softwareStartFromApi(row),
      softwareEndDate: softwareEndFromApi(row),
      subscriptionSourceBillCode: row.subscriptionSourceBillCode,
      subscriptionInvoiceBillCode: row.subscriptionInvoiceBillCode,
      activeUsers: row.activeUsersUsed
        ? `${row.activeUsersUsed} / ${row.activeUsersContract || "-"}`
        : row.activeUsersUsed || "—",
      storage: row.storageUsed
        ? `${row.storageUsed} / ${row.storageContract || "-"}`
        : row.storageUsed || "—",
      aiCredits: row.aiCreditsUsed
        ? `${row.aiCreditsUsed} / ${row.aiCreditsContract || "-"}`
        : row.aiCreditsUsed || "—",
      apiGateway: row.apiGatewayUsed
        ? `${row.apiGatewayUsed} / ${row.apiGatewayContract || "-"}`
        : row.apiGatewayUsed || "—",
      contentCredits: row.contentCreditsUsed
        ? `${row.contentCreditsUsed} / ${row.contentCreditsContract || "-"}`
        : row.contentCreditsUsed || "—",
    }));
  }, [currentUsagePage, page]);

  return {
    // Subscription Results
    subscriptionData,
    subscriptionTotalElements: currentSubPage?.totalElements ?? 0,
    subscriptionTotalPages: currentSubPage?.totalPages ?? 0,
    isSubscriptionsLoading,

    // Usage Results
    usageData,
    usageTotalElements: currentUsagePage?.totalElements ?? 0,
    usageTotalPages: currentUsagePage?.totalPages ?? 0,
    isUsageLoading,
  };
}
